import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
import { ref, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { buildRemoveChipLabel } from "@/shared/composables/removeChipLabel";
import { MultipleSelect } from "@eudonet/opal";
import { logger } from "@/infrastructure/services/LoggerService";
import { searchProjects, searchAllProjects, } from "../../infrastructure/api/projects-api";
import ProjectSeeAllModal from "./ProjectSeeAllModal.vue";
// ─── Props / Emits ────────────────────────────────────────────────────────────
// Thin wrapper over OPAL `MultipleSelect`: OPAL owns the chips, the +N overflow,
// the staged-commit draft (OP-5683), search/loading UI and select-all. This
// component only adapts the OPROJ data source (server-side search by name OR
// code) and coordinates the "Voir tout" See-all modal via `v-model:draft`.
const SEARCH_PAGE_SIZE = 20;
const SEE_ALL_THRESHOLD = 20;
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProjectMultiSelect',
    props: {
        projectIds: {},
        initialProjects: {},
        label: {},
        placeholder: {},
        hint: {},
        disabled: { type: Boolean },
        statuses: {}
    },
    emits: ["update:projectIds"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const removeChipLabel = buildRemoveChipLabel(t);
        // ─── Project cache (id → ProjectOption) ───────────────────────────────────────
        // Every project we have ever seen (initial + each search page). Drives chip
        // labels and the dropdown rows for ids that are not in the current search page.
        const projectCache = ref(new Map());
        function cacheProjects(projects) {
            for (const p of projects)
                projectCache.value.set(p.id, p);
        }
        if (props.initialProjects)
            cacheProjects(props.initialProjects);
        watch(() => props.initialProjects, (projs) => {
            if (projs)
                cacheProjects(projs);
        });
        // ─── Server-side search ────────────────────────────────────────────────────────
        const searchResults = ref([]);
        const searchTotal = ref(0);
        const searchQuery = ref("");
        const isLoading = ref(false);
        let searchToken = 0;
        async function runSearch(query) {
            const token = ++searchToken;
            isLoading.value = true;
            try {
                const page = await searchProjects(query, 1, SEARCH_PAGE_SIZE, props.statuses);
                if (token !== searchToken)
                    return; // discard stale response
                searchResults.value = page.items;
                searchTotal.value = page.totalItems;
                cacheProjects(page.items);
            }
            catch (err) {
                if (token !== searchToken)
                    return;
                logger.error("ProjectMultiSelect: search failed", err);
                searchResults.value = [];
                searchTotal.value = 0;
            }
            finally {
                if (token === searchToken)
                    isLoading.value = false;
            }
        }
        // OPAL debounces and emits `search` in async mode; mount-load seeds the initial
        // (empty-query) page so the list has content the first time it opens.
        function onSearch(query) {
            searchQuery.value = query;
            runSearch(query);
        }
        onMounted(() => runSearch(""));
        // "Select all" across every matching page — OPAL merges the returned ids into
        // the draft. searchAllProjects owns the safeguard cap + logging.
        async function resolveAllMatchingIds() {
            const page = await searchAllProjects(searchQuery.value, undefined, props.statuses);
            cacheProjects(page.items);
            return page.items.map((p) => p.id);
        }
        // ─── Options for OPAL ──────────────────────────────────────────────────────────
        // OPAL renders both chips and rows from `options`, so it must always include the
        // selected/staged projects (for chips) plus the current search page. Selected
        // ids absent from the page are appended from the cache.
        const optionIds = computed(() => {
            const ids = searchResults.value.map((p) => p.id);
            const seen = new Set(ids);
            // Always include the selected/staged ids — even ones not (yet) in the cache —
            // so OPAL can render their chips. Uncached ids fall back to an id-only label
            // below and refine once a search page resolves them.
            for (const id of [...props.projectIds, ...draft.value]) {
                if (!seen.has(id)) {
                    seen.add(id);
                    ids.push(id);
                }
            }
            return ids;
        });
        const options = computed(() => optionIds.value.map((id) => {
            const project = projectCache.value.get(id);
            if (!project) {
                // A selected/staged id we have no cached project for (parent passed
                // projectIds without initialProjects, and it's not in the current page).
                // Render the id so the chip never silently disappears; it refines to
                // the project name once a search page includes it.
                return { id, label: id };
            }
            return {
                id: project.id,
                label: project.name,
                // Dropdown rows show the name only (no code, per product decision); the code stays
                // available as the selected-chip tooltip (tooltipText, a separate affordance).
                tooltipText: project.code,
            };
        }));
        // ─── v-model wiring ──────────────────────────────────────────────────────────
        const selectedIds = computed({
            get: () => props.projectIds,
            set: (ids) => emit("update:projectIds", ids),
        });
        // Shared, uncommitted draft (OPAL `v-model:draft`) — also fed to the See-all
        // modal so neither view loses the other's unconfirmed toggles.
        const draft = ref([...props.projectIds]);
        // ─── See-all modal ─────────────────────────────────────────────────────────────
        const seeAllOpen = ref(false);
        function openSeeAll() {
            seeAllOpen.value = true;
        }
        // Modal "Valider" commits the shared draft, mirroring the dropdown's Confirm.
        function commitFromSeeAll() {
            emit("update:projectIds", [...draft.value]);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(MultipleSelect), {
                    modelValue: selectedIds.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((selectedIds).value = $event)),
                    draft: draft.value,
                    "onUpdate:draft": _cache[1] || (_cache[1] = ($event) => ((draft).value = $event)),
                    options: options.value,
                    "total-count": searchTotal.value,
                    label: __props.label,
                    hint: __props.hint,
                    placeholder: __props.placeholder ?? _unref(t)('campaigns.strategy.projectPicker.placeholder'),
                    "confirm-label": _unref(t)('campaigns.strategy.projectPicker.validate'),
                    "see-all-label": _unref(t)('campaigns.strategy.projectPicker.seeAll'),
                    "see-all-threshold": SEE_ALL_THRESHOLD,
                    "search-clear-label": _unref(t)('common.clearSearch'),
                    "search-placeholder": _unref(t)('campaigns.strategy.projectPicker.searchPlaceholder'),
                    "select-all-label": _unref(t)('campaigns.strategy.projectPicker.selectAll'),
                    "clear-all-label": _unref(t)('campaigns.strategy.projectPicker.clearAll'),
                    "remove-chip-label": _unref(removeChipLabel),
                    "empty-label": _unref(t)('campaigns.strategy.projectPicker.empty'),
                    "loading-label": _unref(t)('campaigns.strategy.projectPicker.loading'),
                    "select-all-resolver": resolveAllMatchingIds,
                    async: true,
                    loading: isLoading.value,
                    "chip-theme": "white",
                    "max-list-height": "202px",
                    disabled: __props.disabled,
                    onSearch: onSearch,
                    onSeeAll: openSeeAll
                }, null, 8 /* PROPS */, ["modelValue", "draft", "options", "total-count", "label", "hint", "placeholder", "confirm-label", "see-all-label", "search-clear-label", "search-placeholder", "select-all-label", "clear-all-label", "remove-chip-label", "empty-label", "loading-label", "loading", "disabled"]),
                (seeAllOpen.value)
                    ? (_openBlock(), _createBlock(ProjectSeeAllModal, {
                        key: 0,
                        modelValue: seeAllOpen.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((seeAllOpen).value = $event)),
                        query: searchQuery.value,
                        statuses: __props.statuses,
                        "staged-ids": draft.value,
                        "onUpdate:stagedIds": _cache[3] || (_cache[3] = ($event) => (draft.value = $event)),
                        onValidate: commitFromSeeAll
                    }, null, 8 /* PROPS */, ["modelValue", "query", "statuses", "staged-ids"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
